import { useTranslation } from 'react-i18next';

import { useErrorMessage } from '@nucleus-commerce/admin-kit/errors';
import type { ApiKey } from '@nucleus-commerce/api-client/admin';
import { isApiError } from '@nucleus-commerce/api-client/transport';
import { Alert, AlertDescription } from '@nucleus-commerce/ui/components/alert';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@nucleus-commerce/ui/components/alert-dialog';

import { useDeleteApiKey, useRevokeApiKey } from '../hooks/use-api-key-mutations';

import type { ApiKeyAction } from './api-key-row-actions';

export interface ConfirmApiKeyDialogProps {
  storeId: string;
  target: { action: Exclude<ApiKeyAction, 'edit'>; key: ApiKey } | null;
  onOpenChange: (open: boolean) => void;
}

/**
 * Revoke keeps the row (and its audit trail) but the key stops working; delete removes the row.
 * Both take effect immediately for every client using the key.
 */
export function ConfirmApiKeyDialog({ storeId, target, onOpenChange }: ConfirmApiKeyDialogProps) {
  const { t } = useTranslation(['apiKeys', 'common', 'errors']);
  const revoke = useRevokeApiKey(storeId);
  const remove = useDeleteApiKey(storeId);
  const message = useErrorMessage();
  const action = target?.action ?? 'revoke';
  const mutation = action === 'revoke' ? revoke : remove;
  const requestId = isApiError(mutation.error) ? mutation.error.requestId : undefined;
  const name = target?.key.name ?? '';

  return (
    <AlertDialog
      open={target !== null}
      onOpenChange={(next) => {
        if (!next) {
          revoke.reset();
          remove.reset();
        }
        onOpenChange(next);
      }}
    >
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{t(`apiKeys:${action}.title`, { name })}</AlertDialogTitle>
          <AlertDialogDescription>{t(`apiKeys:${action}.description`)}</AlertDialogDescription>
        </AlertDialogHeader>
        {mutation.isError ? (
          <Alert variant="destructive" role="alert">
            <AlertDescription>
              {message(mutation.error)}
              {requestId ? (
                <span className="block text-xs">{t('errors:requestIdShort', { requestId })}</span>
              ) : null}
            </AlertDescription>
          </Alert>
        ) : null}
        <AlertDialogFooter>
          <AlertDialogCancel disabled={mutation.isPending}>
            {t('common:actions.cancel')}
          </AlertDialogCancel>
          <AlertDialogAction
            disabled={mutation.isPending}
            onClick={(event) => {
              // Radix closes on click; stay open until the backend has answered.
              event.preventDefault();
              if (!target) return;
              mutation.mutate(target.key.id, { onSuccess: () => onOpenChange(false) });
            }}
          >
            {mutation.isPending ? t(`apiKeys:${action}.submitting`) : t(`apiKeys:${action}.submit`)}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
